/**
 * メインレイアウト (タイトルバー + サイドバー + コンテンツ) - フライト管理アプリ
 * デザイン: DESIGN.md / DESIGN_PRINCIPLES.md 参照
 */
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../services/droneOpsApi';

import { C } from '../ColorTokens';

const NAV_ITEMS = [
  { path: '/',     icon: 'calendar_month', label: 'フライト予約' },
  { path: '/dips', icon: 'airplanemode_active', label: '登録機体一覧' },
];

const ROLE_LABELS = {
  admin:    '管理者',
  operator: 'オペレーター',
  viewer:   '閲覧者',
};

// ---- ヘッダー用アイコンボタン ----
function HeaderIconBtn({ icon, title }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: 'none',
        background: hovered ? 'rgba(0,80,142,0.08)' : 'transparent',
        color: C.onSurfaceVariant,
        cursor: 'pointer',
        transition: 'background 0.15s',
        flexShrink: 0,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 26 }}>{icon}</span>
    </button>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [profile, setProfile] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    userApi.me().then(setProfile).catch(() => {});
  }, []);

  // メニュー外クリックで閉じる
  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  const displayName = profile?.displayName || user?.email?.split('@')[0] || 'User';
  const roleLabel   = ROLE_LABELS[profile?.role] || profile?.role || '';
  const email       = user?.email || '';

  // ドロワー幅: 展開時 248px、折りたたみ時 72px
  const DRAWER_W = collapsed ? 72 : 248;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f8f9ff', overflow: 'hidden' }}>

      {/* ════════════════════════════════════
          タイトルバー
      ════════════════════════════════════ */}
      <header style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        height: 64,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        background: 'rgba(255,255,255,0.70)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.22)',
        boxShadow: '0 4px 24px rgba(0,104,183,0.06)',
        fontFamily: "'Space Grotesk', sans-serif",
      }}>

        {/* 左: サービスアイコン + サービス名 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36,
            borderRadius: 10,
            background: C.primaryContainer,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 20, color: C.onPrimary, fontVariationSettings: "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24" }}
            >
              flight_takeoff
            </span>
          </div>
          <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', color: C.primary }}>
            AIRGROW
          </span>
        </div>

        {/* 右: 通知 | ヘルプ | 縦線 | ユーザー名 | ユーザーアイコン */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

          {/* 通知アイコン */}
          <HeaderIconBtn icon="notifications" title="通知" />

          {/* ヘルプアイコン */}
          <HeaderIconBtn icon="help" title="ヘルプ" />

          {/* 縦セパレータ */}
          <div style={{ width: 1, height: 24, background: C.outlineVariant, margin: '0 6px', flexShrink: 0 }} />

          {/* ユーザー名 + ユーザーアイコン（クリックでメニュー） */}
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setUserMenuOpen(v => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 6px 5px 10px',
                borderRadius: 24,
                border: 'none',
                background: userMenuOpen ? 'rgba(0,80,142,0.08)' : 'transparent',
                cursor: 'pointer',
                transition: 'background 0.15s',
                fontFamily: "'Space Grotesk', sans-serif",
              }}
              onMouseEnter={e => { if (!userMenuOpen) e.currentTarget.style.background = 'rgba(0,80,142,0.06)'; }}
              onMouseLeave={e => { if (!userMenuOpen) e.currentTarget.style.background = 'transparent'; }}
            >
              {/* ユーザー名 */}
              <span style={{
                fontSize: 16, fontWeight: 500, color: C.onSurface,
                maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {displayName}
              </span>
              {/* expand_more */}
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 16, color: C.outline, transition: 'transform 0.2s', transform: userMenuOpen ? 'rotate(180deg)' : 'none' }}
              >
                expand_more
              </span>
              {/* ユーザーアイコン */}
              <div style={{
                width: 32, height: 32,
                borderRadius: '50%',
                background: C.surfaceContainerLow,
                border: `1.5px solid ${C.outlineVariant}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 18, color: C.primary, fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}
                >
                  person
                </span>
              </div>
            </button>

            {/* ユーザーメニュー */}
            {userMenuOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: 220,
                background: 'rgba(255,255,255,0.96)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: 16,
                border: `1px solid ${C.outlineVariant}`,
                boxShadow: '0 8px 32px rgba(0,80,142,0.12)',
                padding: '8px 0',
                zIndex: 100,
                fontFamily: "'Noto Sans JP', sans-serif",
              }}>
                {/* ロール */}
                <div style={{ padding: '10px 20px 4px' }}>
                  <div style={{ fontSize: 10, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, letterSpacing: '0.5px', color: C.primary, textTransform: 'uppercase', marginBottom: 2 }}>
                    Role
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.onSurface }}>
                    {roleLabel || '—'}
                  </div>
                </div>
                {/* メールアドレス */}
                <div style={{ padding: '4px 20px 10px' }}>
                  <div style={{ fontSize: 10, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, letterSpacing: '0.5px', color: C.outline, textTransform: 'uppercase', marginBottom: 2 }}>
                    Email
                  </div>
                  <div style={{ fontSize: 13, color: C.onSurfaceVariant, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {email || '—'}
                  </div>
                </div>
                {/* 横セパレータ */}
                <div style={{ height: 1, background: C.outlineVariant, margin: '0 12px 8px' }} />
                {/* ログアウト */}
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', padding: '10px 20px',
                    border: 'none', background: 'transparent',
                    cursor: 'pointer', color: C.error,
                    fontFamily: "'Noto Sans JP', sans-serif", fontSize: 14,
                    textAlign: 'left', transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(186,26,26,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: C.error }}>logout</span>
                  ログアウト
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ════════════════════════════════════
          タイトルバー下レイアウト
      ════════════════════════════════════ */}
      <div style={{ display: 'flex', flex: 1, marginTop: 64, overflow: 'hidden' }}>

        {/* ════════════════════════════════════
            ドロワー（サンプル準拠デザイン）
        ════════════════════════════════════ */}
        <aside style={{
          position: 'fixed',
          left: 0,
          top: 64,
          bottom: 0,
          width: DRAWER_W,
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 8px',
          background: 'rgba(255,255,255,0.60)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderTopRightRadius: 20,
          borderBottomRightRadius: 20,
          borderRight: '1px solid rgba(255,255,255,0.22)',
          boxShadow: '2px 0 32px rgba(0,40,100,0.08)',
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 500,
          fontSize: 14,
          transition: 'width 0.28s cubic-bezier(0.4,0,0.2,1)',
          overflow: 'hidden',
        }}>

          {/* ナビゲーション */}
          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                title={collapsed ? item.label : undefined}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  padding: collapsed ? '14px 0' : '14px 16px',
                  borderRadius: 12,
                  textDecoration: 'none',
                  color: isActive ? C.onPrimary : C.onSurfaceVariant,
                  background: isActive ? C.primaryContainer : 'transparent',
                  boxShadow: isActive ? '0 4px 12px rgba(0,104,183,0.25)' : 'none',
                  transition: 'background 0.15s, color 0.15s, transform 0.2s, box-shadow 0.15s',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                })}
                onMouseEnter={e => {
                  // アクティブでなければホバーエフェクト + translate
                  const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(0,80,142,0.07)';
                    e.currentTarget.style.color = C.primary;
                    e.currentTarget.style.transform = collapsed ? 'none' : 'translateX(4px)';
                  }
                }}
                onMouseLeave={e => {
                  const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = C.onSurfaceVariant;
                    e.currentTarget.style.transform = 'none';
                  }
                }}
              >
                {({ isActive }) => (
                  <>
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: 22,
                        flexShrink: 0,
                        fontVariationSettings: isActive
                          ? "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24"
                          : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
                      }}
                    >
                      {item.icon}
                    </span>
                    {!collapsed && (
                      <span style={{ fontSize: 14, fontWeight: isActive ? 600 : 500 }}>
                        {item.label}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* ドロワー開閉ボタン（下端） */}
          <div style={{ borderTop: `1px solid rgba(193,199,211,0.35)`, paddingTop: 8, marginTop: 8 }}>
            <button
              onClick={() => setCollapsed(v => !v)}
              title={collapsed ? 'メニューを開く' : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                justifyContent: collapsed ? 'center' : 'flex-start',
                width: '100%',
                padding: collapsed ? '12px 0' : '12px 16px',
                borderRadius: 12,
                border: 'none',
                background: 'transparent',
                color: C.onSurfaceVariant,
                cursor: 'pointer',
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 14,
                fontWeight: 500,
                transition: 'background 0.15s, color 0.15s',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,80,142,0.07)'; e.currentTarget.style.color = C.primary; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.onSurfaceVariant; }}
            >
              {/* ハンバーガー + 方向を示すアイコン */}
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 22, flexShrink: 0 }}
              >
                {collapsed ? 'menu' : 'menu_open'}
              </span>
              {!collapsed && (
                <span>メニューを閉じる</span>
              )}
            </button>
          </div>
        </aside>

        {/* ════════════════════════════════════
            メインコンテンツ
        ════════════════════════════════════ */}
        <main style={{
          marginLeft: DRAWER_W,
          flex: 1,
          overflowY: 'auto',
          transition: 'margin-left 0.28s cubic-bezier(0.4,0,0.2,1)',
          padding: '24px',
        }}>
          <Outlet />
        </main>

      </div>
    </div>
  );
}
